/**
 * Helper to convert hex to RGB.
 * @param {string} hex - The hex color string.
 * @returns {object|null} The RGB object.
 */
export function hexToRgb(hex: string) {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const normalizedHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalizedHex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        }
        : null;
}

/**
 * Helper to convert a single color component to hex.
 * @param {number} component - The color component (0-255).
 * @returns {string} The hex string.
 */
const componentToHex = (component: number) => {
    const hexValue = component.toString(16);
    return hexValue.length === 1 ? `0${hexValue}` : hexValue;
};

/**
 * Helper to convert RGB to hex.
 * @param {number} r - Red component.
 * @param {number} g - Green component.
 * @param {number} b - Blue component.
 * @returns {string} The hex color string.
 */
export function rgbToHex(r: number, g: number, b: number) {
    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`.toUpperCase();
}

/**
 * Clamps a value between 0 and 255.
 * @param {number} value - The value to clamp.
 * @returns {number} The clamped value.
 */
const clamp = (value: number) => Math.max(0, Math.min(255, value));

/**
 * Adjusts brightness of a hex color.
 * @param {string} hex - The hex color string.
 * @param {number} percent - The percentage to adjust.
 * @returns {string} The adjusted hex color string.
 */
export function adjustBrightness(hex: string, percent: number) {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    let { r, g, b } = rgb;
    r = Math.floor((r * (100 + percent)) / 100);
    g = Math.floor((g * (100 + percent)) / 100);
    b = Math.floor((b * (100 + percent)) / 100);

    return rgbToHex(clamp(r), clamp(g), clamp(b));
}

/**
 * Sets alpha for a hex color.
 * @param {string} hex - The hex color string.
 * @param {number} alpha - The alpha value.
 * @returns {string} The RGBA color string.
 */
export function setAlpha(hex: string, alpha: number) {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * Generates full theme set based on base colors.
 * @param {any} baseConfig - The base configuration.
 * @returns {any} The full theme object.
 */
export function generateFullTheme(baseConfig: any) {
    const modes = ['light', 'dark'];
    const fullTheme: any = {};

    modes.forEach((mode) => {
        const base = baseConfig[mode] || {};

        let primary = '#00A79D';
        if (base.primary) {
            primary = base.primary.startsWith('#') ? base.primary : `#${base.primary}`;
        }

        let secondary = '#212529';
        if (base.secondary) {
            secondary = base.secondary.startsWith('#') ? base.secondary : `#${base.secondary}`;
        }

        const primaryHover = base['primary-color-hover'] || adjustBrightness(primary, -10);
        const halfPrimaryHover = base['half-primary-color-hover'] || adjustBrightness(primary, 20);
        const success = base.success || '#2e9e3a';
        const error = base.error || '#f04438';
        const normalizedPrimary = primary;
        const normalizedSecondary = secondary;

        fullTheme[mode] = {
            'primary-color': primary,
            'primary-color-hover': primaryHover,
            'half-primary-color-hover': halfPrimaryHover,
            'secondary-color': secondary,
            success,
            error,
            'tag-error': base['tag-error'] || '#b3261e',
            'white-color': base['white-color'] || (mode === 'light' ? '#f8f9fa' : 'white'),
            'theme-color': base['theme-color'] || (mode === 'light' ? '#f8f9fa' : '#1a2128'),
            'pure-white-color': base['pure-white-color'] || '#ffffff',
            'black-color': base['black-color'] || '#000',
            'success-bg': base['success-bg'] || setAlpha(success, 0.1),
            'error-bg': base['error-bg'] || setAlpha(error, 0.2),
            warning: base.warning || '#f79009',
            'warning-bg': base['warning-bg'] || setAlpha('#f79009', 0.1),
            'calender-allow-columns': base['calender-allow-columns'] || setAlpha(primary, 0.2),
            'green-color': base['green-color'] || normalizedSecondary,
            'blue-color': base['blue-color'] || normalizedPrimary,
            'calender-bg': base['calender-bg'] || (mode === 'light' ? '#e3e8f1' : '#252d34b2'),
            'gradient-green': base['gradient-green'] || (base.primary ? primary : '#6ec282'),
            'gradient-blue': base['gradient-blue'] || '#0099d6',
            'text-color': base['text-color'] || (mode === 'light' ? '#212529' : 'white'),
            'grey-1': base['grey-1'] || (mode === 'light' ? '#6c757d' : 'white'),
            'grey-2': base['grey-2'] || (mode === 'light' ? '#a0a7af' : 'white'),
            'grey-3': base['grey-3'] || (mode === 'light' ? '#d0d5dd' : 'white'),
            'grey-4': base['grey-4'] || (mode === 'light' ? '#e9ecef' : 'white'),
            'grey-5': base['grey-5'] || '#f9f9f9',
            'button-text-color-1':
                base['button-text-color-1'] || (mode === 'light' ? '#f8f9fa' : '#212529'),
            'table-header': base['table-header'] || (mode === 'light' ? '#5e5e5e' : 'white'),
            'table-data': base['table-data'] || (mode === 'light' ? '#292d32' : 'white'),
            'table-border': base['table-border'] || (mode === 'light' ? '#f4f4f4' : 'white'),
            'row-bg': base['row-bg'] || (mode === 'light' ? '#ffffff' : '#f4f4f4'),
            'success-button-bg': base['success-button-bg'] || (mode === 'light' ? primary : 'white'),
            'grey-bg': base['grey-bg'] || (mode === 'light' ? '#ecf5f8' : '#ecf5f8'),
            'grey-select-bg': base['grey-select-bg'] || 'rgba(236, 245, 248, 0.5)',
            'header-bg': base['header-bg'] || (mode === 'light' ? '#ecf5f8b2' : '#ecf5f8b2'),
            'tab-bg': base['tab-bg'] || (mode === 'light' ? '#ecf5f899' : '#ecf5f899'),
            'border-grey': base['border-grey'] || '#e3e8f1',
            'border-grey-1': base['border-grey-1'] || (mode === 'light' ? '#e9e9ec' : '#e3e8f1'),
            'modal-box-body-bg': base['modal-box-body-bg'] || '#e4ebebcf',
            'modal-box-header-bg': base['modal-box-header-bg'] || '#e9f0f3',
            'modal-box-bg': base['modal-box-bg'] || '#0909094f',
            'modal-box-container-bg':
                base['modal-box-container-bg'] || (mode === 'light' ? '#f9f9f999' : '#252d34b2'),
            'dropdown-bg': base['dropdown-bg'] || '#f1f7f9d7',
            'selected-bg': base['selected-bg'] || '#d0d5dd',
            'border-gradient-color':
                base['border-gradient-color'] ||
                `linear-gradient(to right, ${primaryHover} 0%, ${success} 100%) 1`,
            'card-border-gradient-color':
                base['card-border-gradient-color'] ||
                `linear-gradient(to right, ${primaryHover} 0%, ${success} 100%)`,
            'card-bg-gradient-color': base['card-bg-gradient-color'] || '#eef6f9',
            'card-bg-hover-gradient-color':
                base['card-bg-hover-gradient-color'] ||
                `linear-gradient(to top left, ${setAlpha(primary, 0.2)} 0%, ${setAlpha(success, 0.2)} 100%)`,
            'background-gradient-color':
                base['background-gradient-color'] ||
                `linear-gradient(to top left, ${setAlpha(success, 0.18)} 0%, ${setAlpha(primary, 0.27)} 100%)`,
            'multi-select-border':
                base['multi-select-border'] || (mode === 'light' ? '#e9ecef' : '#6c757d'),
            'toggle-bg': base['toggle-bg'] || (mode === 'light' ? '#f9f9f999' : '#252d34b2'),
        };
    });
    return fullTheme;
}
